/**
 * Estructura en espiral — alterna contexto y práctica en guías largas.
 * Evita el "vertedero" de teoría al inicio y ejercicios sueltos al final.
 */

import type { GameMode } from '../types/gameMode.types';
import type { GuideBlockLike } from './humanitiesGuideEnforcer';

export const SPIRAL_BLOCK_THRESHOLD = 7;

const CONTEXT_MODES = new Set<string>([
  'info_block',
  'destacado',
  'figura',
  'formula_destacada',
  'canvas_breakdown',
  'subtitulo',
  'structural_breakdown',
]);

const PRACTICE_MODES = new Set<GameMode>([
  'arena',
  'completar',
  'vincula',
  'agrupa',
  'order',
  'desarrollo',
  'sandbox',
  'geometry_canvas',
  'source_analysis',
  'poll',
]);

const MAX_CONTEXT_PER_CYCLE = 2;
const MAX_PRACTICE_PER_CYCLE = 2;

function isContextBlock(mode: string): boolean {
  return CONTEXT_MODES.has(mode);
}

function isPracticeBlock(mode: string): boolean {
  return PRACTICE_MODES.has(mode as GameMode);
}

function partitionTail(blocks: GuideBlockLike[]) {
  const glossaries: GuideBlockLike[] = [];
  const body: GuideBlockLike[] = [];
  for (const block of blocks) {
    if (block.mode === 'glosario_tabla') glossaries.push(block);
    else body.push(block);
  }
  return { body, glossaries };
}

/** Reparte contenido restante entre subtítulos en lugar de amontonarlo al final. */
export function distributeContentAcrossHeaders<T extends GuideBlockLike>(blocks: T[]): T[] {
  const headers: T[] = [];
  const formulas: T[] = [];
  const contents: T[] = [];

  for (const block of blocks) {
    if (block.mode === 'subtitulo') headers.push(block);
    else if (block.mode === 'formula_destacada') formulas.push(block);
    else if (block.mode !== 'glosario_tabla') contents.push(block);
  }

  if (!headers.length) return blocks;

  const glossaries = blocks.filter((b) => b.mode === 'glosario_tabla');
  const result: T[] = [];
  let contentIdx = 0;

  const firstContentOriginal = blocks.findIndex(
    (b) => b.mode !== 'subtitulo' && b.mode !== 'glosario_tabla' && b.mode !== 'formula_destacada',
  );
  const firstHeaderOriginal = blocks.findIndex((b) => b.mode === 'subtitulo');
  const introBeforeHeader =
    firstContentOriginal >= 0 &&
    (firstHeaderOriginal < 0 || firstContentOriginal < firstHeaderOriginal);

  if (introBeforeHeader && contentIdx < contents.length) {
    result.push(contents[contentIdx++]!);
  }

  for (let i = 0; i < headers.length; i++) {
    result.push(headers[i]!);
    const headersLeft = headers.length - i;
    const contentsLeft = contents.length - contentIdx;
    const take = Math.max(1, Math.ceil(contentsLeft / headersLeft));
    for (let j = 0; j < take && contentIdx < contents.length; j++) {
      result.push(contents[contentIdx++]!);
    }
    if (i === 0 && formulas.length) {
      result.push(formulas.shift()!);
    } else if (formulas.length && i === headers.length - 1) {
      result.push(...formulas);
      formulas.length = 0;
    }
  }

  while (contentIdx < contents.length) {
    result.push(contents[contentIdx++]!);
  }
  result.push(...formulas, ...glossaries);

  return result;
}

function interleaveContextAndPractice<T extends GuideBlockLike>(blocks: T[]): T[] {
  const context: T[] = [];
  const practice: T[] = [];

  for (const block of blocks) {
    if (isPracticeBlock(block.mode)) practice.push(block as T);
    else context.push(block as T);
  }

  const out: T[] = [];
  let ci = 0;
  let pi = 0;

  if (context[0]?.mode === 'info_block') {
    out.push(context[0]!);
    ci = 1;
  }

  while (ci < context.length || pi < practice.length) {
    for (let i = 0; i < MAX_CONTEXT_PER_CYCLE && ci < context.length; i++) {
      out.push(context[ci++]!);
    }
    for (let i = 0; i < MAX_PRACTICE_PER_CYCLE && pi < practice.length; i++) {
      out.push(practice[pi++]!);
    }
  }

  return out;
}

export function detectTheoryDump(blocks: GuideBlockLike[]): boolean {
  if (blocks.length < SPIRAL_BLOCK_THRESHOLD) return false;

  let maxContextRun = 0;
  let run = 0;
  let seenPractice = false;

  for (const block of blocks) {
    if (isPracticeBlock(block.mode)) {
      seenPractice = true;
      run = 0;
      continue;
    }
    if (block.mode === 'glosario_tabla') continue;
    run += 1;
    maxContextRun = Math.max(maxContextRun, run);
  }

  if (seenPractice && maxContextRun >= 4) return true;
  if (!seenPractice && maxContextRun >= 3) return true;

  const firstPracticeIdx = blocks.findIndex((b) => isPracticeBlock(b.mode));
  if (seenPractice && firstPracticeIdx > Math.floor(blocks.length * 0.55)) return true;

  return false;
}

export function enforceGuideSpiralStructure<T extends GuideBlockLike>(blocks: T[]): T[] {
  if (blocks.length < SPIRAL_BLOCK_THRESHOLD) return blocks;

  const { body, glossaries } = partitionTail(blocks);
  const hasPractice = body.some((b) => isPracticeBlock(b.mode));

  let ordered: T[];
  if (hasPractice) {
    ordered = interleaveContextAndPractice(body) as T[];
  } else {
    ordered = distributeContentAcrossHeaders(body) as T[];
  }

  return [...ordered, ...(glossaries as T[])];
}

export { isContextBlock, isPracticeBlock, PRACTICE_MODES, CONTEXT_MODES };
